'use client';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import type { EditorView } from '@tiptap/pm/view';
import { type Editor, type EditorOptions, type Extension, useEditor } from '@tiptap/react';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import type { TextPromptWithInputItem } from '@/app/types';
import { editorJsonToTextPrompt, getTextPromptLength, textPromptToHTML, truncateContentToMaxLength } from '@/app/utils';

// 获取当前内容和长度的辅助函数
const getCurrentContentInfo = (view: EditorView) => {
  const currentContent = editorJsonToTextPrompt(view.state.doc.toJSON());
  const currentLength = getTextPromptLength(currentContent);
  return { currentContent, currentLength };
};

// 计算选中文本长度的辅助函数
const getSelectedTextLength = (view: EditorView) => {
  const { from, to } = view.state.selection;
  if (from === to) return 0;

  const selectedText = view.state.doc.textBetween(from, to);
  return selectedText.length;
};

const setEditorContentSafely = (editor: Editor, content: string, emitUpdate = true) => {
  if (editor.isDestroyed) return;

  const wasFocused = editor.isFocused;
  if (wasFocused) {
    editor.commands.blur();
  }

  editor.commands.setContent(content, emitUpdate);

  if (wasFocused && !editor.isDestroyed) {
    editor.commands.focus('end');
  }
};

// 检查长度限制的通用函数
const checkLengthLimit = (view: EditorView, inputLength: number, maxLength: number, onExceed: () => void): boolean => {
  if (!maxLength) return false;

  const { currentLength } = getCurrentContentInfo(view);
  const selectedLength = getSelectedTextLength(view);

  // 计算输入后的长度：当前长度 - 选中文本长度 + 输入长度
  const newLength = currentLength - selectedLength + inputLength;

  if (newLength > maxLength) {
    onExceed();
    return true; // 超出限制
  }

  return false; // 未超出限制
};

interface UseEditorWithExtensionsProps {
  content: TextPromptWithInputItem;
  updateContent: (value: TextPromptWithInputItem) => void;
  maxLength?: number;
  onMaxLengthExceeded?: () => void;
  paragraphClassName?: string;
  extensions?: Extension[];
  editorProps?: Partial<EditorOptions['editorProps']>;
  editable?: boolean;
  onUpdateCallback?: (
    editor: Editor,
    lastValidContent: MutableRefObject<string | null>,
    isInternalUpdate: MutableRefObject<boolean>,
  ) => boolean;
}

/**
 * 受控的富文本编辑器，并限制内容长度
 * @param content - 内容
 * @param updateContent - 更新内容
 * @param maxLength - 文本内容最大长度
 * @param onMaxLengthExceeded - 超出最大长度时的回调
 * @param paragraphClassName - 段落类名
 * @param extensions - 扩展
 * @param editorProps - 编辑器属性
 * @param onUpdateCallback - 更新回调
 */
export const useEditorWithExtensions = ({
  content,
  updateContent,
  maxLength,
  onMaxLengthExceeded,
  paragraphClassName,
  extensions = [],
  editorProps = {},
  editable = true,
  onUpdateCallback,
}: UseEditorWithExtensionsProps) => {
  const isTextPromptEmpty = (promptContent: TextPromptWithInputItem): boolean => {
    return getTextPromptLength(promptContent) === 0;
  };

  const [isEmpty, setIsEmpty] = useState(() => isTextPromptEmpty(content));
  const isInternalUpdate = useRef(false);
  const lastValidContent = useRef<string | null>(null);
  const isComposing = useRef(false); // 标记是否正在输入法输入过程中
  const compositionStartLength = useRef<number>(0); // 记录输入法开始时的内容长度

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    editorProps: {
      ...editorProps,
      // 合并用户传入的 handleKeyDown
      handleKeyDown: (view, event) => {
        // 先执行用户自定义的 handleKeyDown
        const userHandled = editorProps.handleKeyDown?.(view, event);
        if (userHandled) return true;

        // 检查长度限制（对于单字符输入）
        if (maxLength && event?.key?.length === 1 && !event.ctrlKey && !event.metaKey) {
          const isExceeded = checkLengthLimit(view, event.key.length, maxLength, () => {
            event.preventDefault();
            onMaxLengthExceeded?.();
          });

          if (isExceeded) return true;
        }

        return false;
      },
      // 合并用户传入的 handleDOMEvents
      handleDOMEvents: {
        ...editorProps.handleDOMEvents,
        compositionstart: (view, event) => {
          // 先执行用户自定义的 compositionstart
          const userHandled = editorProps.handleDOMEvents?.compositionstart?.(view, event);
          if (userHandled) return true;

          // 标记开始输入法输入，并记录当前长度
          isComposing.current = true;
          if (maxLength) {
            const { currentLength } = getCurrentContentInfo(view);
            compositionStartLength.current = currentLength;
          }
          return false;
        },
        compositionend: (view, event) => {
          // 先执行用户自定义的 compositionend
          const userHandled = editorProps.handleDOMEvents?.compositionend?.(view, event);
          if (userHandled) return true;

          // 标记输入法输入结束
          isComposing.current = false;

          // 检查输入法输入的长度限制
          if (maxLength && editor && event.data) {
            // 使用 setTimeout 确保输入法输入完成后再检查长度
            // 这样可以在输入完成后截断，而不是阻止输入
            setTimeout(() => {
              const { currentContent, currentLength } = getCurrentContentInfo(view);
              const inputLength = currentLength - compositionStartLength.current;

              // 只有当确实有新增内容且超出限制时才截断
              if (inputLength > 0 && currentLength > maxLength) {
                // 超出长度限制，截断内容到最大长度（保留用户的删除操作，只截断超出的部分）
                const truncatedContent = truncateContentToMaxLength(currentContent, maxLength);
                const truncatedHtml = textPromptToHTML(truncatedContent);

                // 设置截断后的内容
                isInternalUpdate.current = true;
                setEditorContentSafely(editor, truncatedHtml);
                updateContent(truncatedContent);
                setIsEmpty(isTextPromptEmpty(truncatedContent));
                lastValidContent.current = truncatedHtml;
                onMaxLengthExceeded?.();

                setTimeout(() => {
                  isInternalUpdate.current = false;
                }, 0);
              }
            }, 0);
          }

          return false;
        },
      },
      // 合并用户传入的 handlePaste
      handlePaste: (view, event, slice) => {
        // 先执行用户自定义的 handlePaste
        const userHandled = editorProps.handlePaste?.(view, event, slice);
        if (userHandled) return true;

        // 检查粘贴内容长度限制
        if (maxLength && editor) {
          const { currentLength } = getCurrentContentInfo(view);
          const selectedLength = getSelectedTextLength(view);

          // 计算粘贴后的可用长度
          const availableLength = maxLength - currentLength + selectedLength;

          if (availableLength <= 0) {
            // 即使替换选中文本也无法容纳任何新内容
            onMaxLengthExceeded?.();
            return true;
          }

          // 获取粘贴的文本内容
          const pasteText = event.clipboardData?.getData('text/plain') || '';

          if (pasteText.length > availableLength) {
            // 粘贴内容超出可用长度，截断粘贴内容
            const truncatedText = pasteText.substring(0, availableLength);

            // 阻止默认粘贴行为
            event.preventDefault();

            // 将截断后的文本转换为 HTML，保留换行符
            // 将换行符转换为段落分隔，这样 insertContent 才能正确保留换行
            const htmlContent = truncatedText
              .split('\n')
              .map((line) => `<p>${line}</p>`)
              .join('');

            // 手动插入截断后的内容（会自动替换选中的文本）
            editor.commands.insertContent(htmlContent);

            onMaxLengthExceeded?.();
            return true;
          }
        }

        return false;
      },
    },
    parseOptions: {
      preserveWhitespace: 'full',
    },
    extensions: [
      Document,
      Paragraph.configure({ HTMLAttributes: { class: paragraphClassName } }),
      Text,
      History,
      ...extensions,
    ],
    content: textPromptToHTML(content),
    onUpdate: ({ editor }) => {
      // 自定义回调
      if (onUpdateCallback) {
        const canUpdate = onUpdateCallback(editor, lastValidContent, isInternalUpdate);
        if (!canUpdate) {
          return;
        }
      }

      const updatedContent = editorJsonToTextPrompt(editor.getJSON());

      // 检查内容是否为空
      setIsEmpty(isTextPromptEmpty(updatedContent));

      // 检查内容长度（输入法输入过程中跳过检查，允许临时溢出）
      if (maxLength && !isComposing.current) {
        const contentLength = getTextPromptLength(updatedContent);

        if (contentLength > maxLength) {
          onMaxLengthExceeded?.();

          // 截断内容到最大长度（保留用户的删除操作，只截断超出的部分）
          const truncatedContent = truncateContentToMaxLength(updatedContent, maxLength);
          const truncatedHtml = textPromptToHTML(truncatedContent);

          // 设置截断后的内容
          isInternalUpdate.current = true;
          setEditorContentSafely(editor, truncatedHtml);
          updateContent(truncatedContent);
          setIsEmpty(isTextPromptEmpty(truncatedContent));
          lastValidContent.current = truncatedHtml;

          setTimeout(() => {
            isInternalUpdate.current = false;
          }, 0);

          return;
        }
      }

      isInternalUpdate.current = true;
      updateContent(updatedContent);

      // 只在非输入法输入过程中更新有效内容
      if (!isComposing.current) {
        lastValidContent.current = editor.getHTML();
      }

      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 0);
    },
  });

  useEffect(() => {
    if (!editor) return;

    // 外部更新 content 时，更新 editor 内容
    if (!isInternalUpdate.current) {
      // 使用 setTimeout 修复 Warning: flushSync was called from inside a lifecycle method
      setTimeout(() => {
        if (editor.isDestroyed) return;
        const newHtml = textPromptToHTML(content);
        const nextIsEmpty = isTextPromptEmpty(content);

        // content 变化时要始终同步空态，不能因为 HTML 一致就跳过
        setIsEmpty(nextIsEmpty);

        // 跳过不需要更新的情况（内容未变化）
        if (editor.getHTML() === newHtml) {
          lastValidContent.current = editor.getHTML();
          return;
        }

        setEditorContentSafely(editor, newHtml, false); // false 避免触发 onUpdate

        lastValidContent.current = editor.getHTML();
      }, 0);
    }
  }, [content, editor]);

  return {
    editor,
    isEmpty,
    isInternalUpdate,
    lastValidContent,
  };
};
